import { createSelector } from "@reduxjs/toolkit";
import { sortBy } from "lodash-es";

import { BulkImportJobType, OrgUserType } from "components/server-types";
import { RootState } from "store";

export const createAppSelector = createSelector.withTypes<RootState>();

// This file only has the most basic selectors that do not have parameters, and do not filter
// or join objects.

// Single attribute selectors //

export const selectCurrentUserId = (state: RootState) => state.auth.user_id;
export const selectUserJWT = (state: RootState) => state.auth.collaboration_jwt;

// This needs to be in base.ts instead of view.ts to avoid circular dependencies
export const selectCurrentSectionId = (state: RootState) => state.view.currentSectionId;

export const selectClassUsers = createAppSelector([(state) => state.class_users], (class_users) =>
  Object.values(class_users),
);

// Pubsub Slice Selectors //
export const selectSections = createAppSelector([(state) => state.sections], (sections) =>
  Object.values(sections),
);

export const selectSectionMap = (state) => state.sections;

/** Caution: This selector returns *all* section users, including disabled ones.
 * If you need only section users that are not disabled, use `selectEnabledSectionUsers` instead.
 */
export const selectSectionUsers = createAppSelector(
  [(state) => state.section_users],
  (section_users) => Object.values(section_users),
);

export const selectSectionUserMap = (state: RootState) => state.section_users;

export const selectClassSessions = createAppSelector(
  [(state) => state.class_sessions],
  (class_sessions) => Object.values(class_sessions),
);

export const selectClassSessionSummaries = createAppSelector(
  [(state) => state.class_session_summaries],
  (class_session_summaries) => Object.values(class_session_summaries),
);

export const selectClassSessionUserSummaries = createAppSelector(
  [(state) => state.class_session_user_summaries],
  (class_session_user_summaries) => Object.values(class_session_user_summaries),
);

export const selectUsers = createAppSelector([(state) => state.users], (users) =>
  Object.values(users),
);

export const selectTopics = createAppSelector([(state) => state.topics], (topics) =>
  Object.values(topics),
);

export const selectActivities = createAppSelector([(state) => state.activities], (activities) =>
  Object.values(activities),
);

export const selectActivityMap = (state: RootState) => state.activities;

export const selectPages = createAppSelector([(state) => state.pages], (pages) =>
  Object.values(pages),
);

export const selectTerms = createAppSelector([(state) => state.terms], (terms) =>
  Object.values(terms),
);

export const selectTermMap = (state: RootState) => state.terms;

export const selectCourses = createAppSelector([(state) => state.courses], (courses) =>
  Object.values(courses),
);

export const selectHasCourses = createAppSelector([selectCourses], (courses) => courses.length > 0);

export const selectCourseGroupings = createAppSelector(
  [(state) => state.course_groupings],
  (course_groupings) => Object.values(course_groupings),
);

// This returns all orgs, including disabled ones.
export const selectOrgs = createAppSelector([(state) => state.orgs], (orgs) =>
  sortBy(Object.values(orgs), (org) => org.name.toLowerCase()),
);

export const selectOrgUsers = createAppSelector(
  [(state) => state.org_users],
  (org_users): OrgUserType[] => Object.values(org_users),
);

export const selectOutcomes = createAppSelector([(state) => state.outcomes], (outcomes) =>
  sortBy(Object.values(outcomes), (o) => o.title.toLowerCase()),
);

export const selectOutcomeSubgroups = createAppSelector(
  [(state) => state.outcome_subgroups],
  (outcome_subgroups) => sortBy(Object.values(outcome_subgroups), (os) => os.title.toLowerCase()),
);

export const selectOutcomeGroups = createAppSelector(
  [(state) => state.outcome_groups],
  (outcome_groups) => sortBy(Object.values(outcome_groups), (og) => og.title.toLowerCase()),
);

export const selectOutcomeById = createAppSelector(
  [(state) => state.outcomes, (state, outcomeId: string) => outcomeId],
  (outcomes, outcomeId) => outcomes[outcomeId],
);

export const selectSectionOutcomes = createAppSelector(
  [(state) => state.section_outcomes],
  (section_outcomes) => Object.values(section_outcomes),
);

export const selectSectionOutcomeMap = (state: RootState) => state.section_outcomes;

export const selectPageOutcomes = createAppSelector(
  [(state) => state.page_outcomes],
  (page_outcomes) => Object.values(page_outcomes),
);

export const selectPageGroups = createAppSelector([(state) => state.page_groups], (page_groups) =>
  Object.values(page_groups),
);

export const selectPageGroupUsers = createAppSelector(
  [(state) => state.page_group_users],
  (page_group_users) => Object.values(page_group_users),
);

export const selectOutcomeAssessments = createAppSelector(
  [(state) => state.outcome_assessments],
  (outcome_assessments) => Object.values(outcome_assessments),
);

export const selectAssessments = createAppSelector([(state) => state.assessments], (assessments) =>
  Object.values(assessments),
);

export const selectSubmissions = createAppSelector([(state) => state.submissions], (submissions) =>
  Object.values(submissions),
);

export const selectChatMessages = createAppSelector(
  [(state) => state.chat_messages],
  (chat_messages) => Object.values(chat_messages),
);

export const selectBulkImportJobById = createAppSelector(
  [(state) => state.bulk_import_jobs, (state, jobId: string) => jobId],
  (bulk_import_jobs, jobId) => bulk_import_jobs[jobId] as BulkImportJobType | undefined,
);

export const selectBreakoutGroups = createAppSelector(
  [(state) => state.breakout_groups],
  (breakout_groups) => Object.values(breakout_groups),
);
